import React, { useMemo } from "react";
import Loader from "../../../../../common/Loader/Loader";
import { useDietPlanTabContext } from "./useDietPlanTab";
import DateBox from "../../../../../common/DateBox/DateBox";
import { Toaster } from "../../../../../common/Toaster/Toaster";
import DietMealSlotEditor from "../../../../../features/diet/workflow/meal-slot/modify/DietMealSlotEditor";
import { FileDown, Layers, Loader2 } from "lucide-react";
import { usePendingSyncTotal } from "../../../../../hooks/usePendingSyncTotal";
import ReusableTable from "../../../../../common/ReusableTable/ReusableTable";
import CompactNutrientSummaryCard from "../../../ui/nutrition/CompactNutritionCard";
import { MEALPERIODS } from "../../../core/utils/mealTypes";
import { formatDate } from "../../../../../utils/validation.util";
import { normalizeKey } from "../../../core/utils/normalizeDietKey";
import {
  extractOptionNumbersFromOptionIds,
  filterFoodsByOptionNumber,
} from "../../../core/diet-plan/mealDataByPeriod";
import { optionIdsHas } from "../../../core/utils/formatOptionTitle";
import { prefetchTargetPlanDetails } from "../../../core/diet-plan/targetPlanPrefetch";
import MealSlotEditorToolbar from "../../../ui/meal-slot/shell/MealSlotEditorToolbar";
import MealSlotViewModeToggle from "../../../ui/meal-slot/shell/MealSlotViewModeToggle";
import MealSlotOptionFilterBar from "../../../ui/meal-slot/shell/MealSlotOptionFilterBar";
import MealSlotMealTypeFilterBar from "../../../ui/meal-slot/shell/MealSlotMealTypeFilterBar";
import MealSlotOptionsFirstRows from "../../../ui/meal-slot/shell/MealSlotOptionsFirstRows";
import MealSlotMealTypesFirstRows from "../../../ui/meal-slot/shell/MealSlotMealTypesFirstRows";
import MealSlotChoiceModal from "../../../ui/meal-slot/shell/MealSlotChoiceModal";
import { getRowNutrition } from "../../../ui/meal-slot/shell/formatRowCalories";
import PendingSyncIndicator from "../../../../../common/PendingSyncIndicator/PendingSyncIndicator";
import CopyDietPlanModal from "../modify/CopyDietPlanModal";
import SaveAsTemplateModal from "../modify/SaveAsTemplateModal";
import NutrientSummaryCard from "./NutrientSummaryCard";
import ActivePlanBadge from "../../../ui/nutrition/ActivePlanBadge";
import { resolvePlanStatus } from "../../../core/diet-plan/resolvePlanStatus";

export default function DietPlanView() {
  const m = useDietPlanTabContext();
  const {
    profileId, isPlanDetailPage, visibleDates,
    selectedDate,
    nutritionData, setNutritionData, plannedData,
    isPlannedView, isLoading, isTrackingLoading, isInitialLoad,
    openTracked, setOpenTracked, openPlanned, setOpenPlanned, toastMessage, setToastMessage,
    toastType, setToastType, navigate, dietCommonUtils, eventTypeId,
    dietEventTypeId, planDetails, planDetailsLoading, selectedOptionFilter,
    setSelectedOptionFilter, planViewMode, setPlanViewMode, selectedMealTypeFilter,
    setSelectedMealTypeFilter, addChoiceModal, setAddChoiceModal,
    editPlanModalOpen, setEditPlanModalOpen, editingPlanId, editPlanName,
    setEditPlanName, editPlanStartDate, setEditPlanStartDate, editPlanEndDate, setEditPlanEndDate,
    editPlanSaving, editPlanError, setEditPlanError, isDownloadingPDF,
    pdfModalOpen, pdfBlobUrl,
    plansTableList, plansTableLoading, plansTableError, deleteConfirmMessage,
    isDeletingPlan, planDataByPeriod, planMealIdMap, planMealWindows, handleUpdateMealIntakeWindow,
    planAvailableOptions, handlePlanPeriodDataChange, activePlanId,
    handleAddMealTypeToPlan, handlePlanAddOptionAll, handlePlanAddOptionToMealType,
    planEmptyOptions, planDisplayOptions, isAddingOption, isAddingMealType,
    optionDeleteConfirm, isDeletingOption, requestPlanDeleteOptionConfirm,
    handleConfirmDeletePlanOption, handleCancelDeletePlanOption,
    transformTotalsForCompactCard, planSummary, planToolbarNutrition, planDisplayMeta,
    formatDateForDisplay,
    handleConfirmDeletePlan, handleCancelDeletePlan, plansTableColumns, closePdfModal,
    handleDownloadPdfFromModal, handleChangeTracked, prefetchTrackingData, handleChangePlanned, handleOnSelectDate,
    handleChangeDiet, renderMealSection, handleViewPDF,
    handleSaveEditPlan, cloningPlanId, copySourcePlan, handleCloseCopyModal,
    handleCopyPlanContinue, refetchPlansTable,
    savingAsTemplatePlan, savingAsTemplate, saveAsTemplatePhase, saveAsTemplateError,
    partialTemplateId, existingTemplateNames,
    handleOpenSaveAsTemplate, handleCloseSaveAsTemplateModal, handleSaveAsTemplateContinue,
    handleOpenPartialTemplate, planPeriods,
  } = m;

  const { total: pendingSyncTotal } = usePendingSyncTotal(
    activePlanId ? `plan-${activePlanId}` : null
  );

  const todayStr = formatDate(new Date());
  const activePlanStatus = useMemo(
    () => resolvePlanStatus(plansTableList, todayStr),
    [plansTableList, todayStr]
  );

  if (isLoading && isInitialLoad) return <Loader />;

  // -------------------- Main Render --------------------
  return (
    <div className="w-full app-bg min-h-screen pb-8">
      {(toastMessage || deleteConfirmMessage) && (
        <Toaster
          message={toastMessage || deleteConfirmMessage}
          type={deleteConfirmMessage ? "warning" : toastType}
          onConfirm={deleteConfirmMessage ? handleConfirmDeletePlan : undefined}
          onCancel={deleteConfirmMessage ? handleCancelDeletePlan : undefined}
          confirmLoading={isDeletingPlan}
        />
      )}

      {optionDeleteConfirm.open && (
        <Toaster
          message={optionDeleteConfirm.message}
          onConfirm={handleConfirmDeletePlanOption}
          onCancel={handleCancelDeletePlanOption}
          confirmLoading={isDeletingOption}
        />
      )}

      {/* Full-page loading overlay while an option or meal type is being added – blocks all interaction */}
      {(isAddingOption || isAddingMealType) && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-white/85 backdrop-blur-sm"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-12 h-12 text-orange-500 animate-spin" />
            <p className="text-gray-700 font-medium">
              {isAddingMealType ? "Adding meal type…" : "Adding option…"}
            </p>
          </div>
        </div>
      )}

      {/* Header: hide on plan detail page (we only show the selected plan) */}
      {!isPlanDetailPage && (
        <div className="flex flex-wrap justify-between items-center gap-4 mb-4 px-0">
          <div className="flex gap-3">
            <button
              className={`w-[180px] ${!isPlannedView
                ? "bg-dark-orange text-white"
                : "bg-white text-gray-700"
                } px-6 py-3 rounded-xl font-bold shadow-md hover:shadow-lg transition-all duration-200`}
              onClick={handleChangeTracked}
              onMouseEnter={prefetchTrackingData}
            >
              Tracked
            </button>
            <button
              className={`w-[180px] ${isPlannedView
                ? "bg-dark-orange text-white"
                : "bg-white text-gray-700"
                } px-6 py-3 rounded-xl font-bold shadow-md hover:shadow-lg transition-all duration-200`}
              onClick={handleChangePlanned}
            >
              Planned
            </button>
          </div>
          <button
            className="w-[200px] bg-orange-primary text-white px-6 py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200"
            onClick={handleChangeDiet}
          >
            Add Diet Plan
          </button>
        </div>
      )}

      {/* User page: show Nutrition Information card on both Tracked and Planned tabs */}
      {!isPlanDetailPage && (
        <div className="mb-6">
          <NutrientSummaryCard
            eventId={eventTypeId}
            profileId={profileId}
            setToastMessage={setToastMessage}
            setToastType={setToastType}
            totals={nutritionData.totals}
            settings={nutritionData.settings}
            isTotalsLoading={isLoading || isTrackingLoading}
            title={isPlannedView ? "Plan Vs Goal" : "Tracked Vs Goal"}
            titleTooltip={
              isPlannedView
                ? "Average of macros across all meal options assigned for this date range."
                : "Sum of macros from foods logged for this date."
            }
            activePlanBadge={
              isPlannedView ? (
                <ActivePlanBadge
                  status={activePlanStatus.status}
                  planTitle={activePlanStatus.plan?.title}
                  startDate={activePlanStatus.plan?.start_date}
                  endDate={activePlanStatus.plan?.end_date}
                />
              ) : null
            }
            onSettingsSaved={(vals) =>
              setNutritionData((prev) => ({ ...prev, settings: vals }))
            }
          />
        </div>
      )}

      {/* Planned tab: plans table; click row to open plan on its own page (like templates) */}
      {isPlannedView && !isPlanDetailPage && (
        <div className="mb-6 px-2">
          <p className="text-sm text-gray-500 mb-3">Plans for this user. Click a row to view details.</p>
          {plansTableLoading ? (
            <div className="flex justify-center py-8">
              <Loader />
            </div>
          ) : plansTableError ? (
            <div className="py-8 text-center rounded-xl border border-amber-200 bg-amber-50">
              <p className="text-amber-800 mb-3">{plansTableError}</p>
              <button
                type="button"
                onClick={() => refetchPlansTable(profileId)}
                className="px-4 py-2 rounded-lg bg-orange-primary text-white font-medium hover:bg-orange-600"
              >
                Retry
              </button>
            </div>
          ) : plansTableList.length === 0 ? (
            <div className="py-8 text-center rounded-xl border border-gray-200 bg-gray-50">
              <p className="text-gray-600 mb-2">No diet plans yet.</p>
              <button
                type="button"
                onClick={handleChangeDiet}
                className="px-4 py-2 rounded-lg bg-orange-primary text-white font-medium hover:bg-orange-600"
              >
                Add Diet Plan
              </button>
            </div>
          ) : (
            <ReusableTable
              columns={plansTableColumns}
              data={plansTableList}
              initialState={{ sorting: [{ id: "start_date", desc: true }] }}
              enableSorting={true}
              enableFiltering={false}
              enableResizing={true}
              enablePagination={false}
              onRowClick={(row) => {
                // Warm plan details while navigating (prefetch cache is reused on detail page).
                if (dietEventTypeId && profileId && row.id) {
                  prefetchTargetPlanDetails(dietEventTypeId, row.id, profileId);
                }
                navigate(`/dashboard/${profileId}/diet-plan/${row.id}`, {
                  state: {
                    planMeta: {
                      [row.id]: {
                        title: row.title || "",
                        durationId: row.duration_id,
                        active_from: row.start_date,
                        active_to: row.end_date,
                      },
                    },
                  },
                });
              }}
              emptyMessage="No diet plans yet."
            />
          )}
        </div>
      )}

      <CopyDietPlanModal
        isOpen={Boolean(copySourcePlan)}
        onClose={handleCloseCopyModal}
        onContinue={handleCopyPlanContinue}
        planTitle={copySourcePlan?.title || ""}
        existingPlans={plansTableList}
        continuing={Boolean(cloningPlanId)}
      />

      <SaveAsTemplateModal
        isOpen={Boolean(savingAsTemplatePlan)}
        onClose={handleCloseSaveAsTemplateModal}
        onContinue={handleSaveAsTemplateContinue}
        planTitle={savingAsTemplatePlan?.title || ""}
        dietCommonUtils={dietCommonUtils}
        existingTemplateNames={existingTemplateNames}
        continuing={savingAsTemplate}
        phase={saveAsTemplatePhase}
        errorMessage={saveAsTemplateError}
        partialTemplateId={partialTemplateId}
        onOpenPartialTemplate={handleOpenPartialTemplate}
      />

      {/* Edit plan name & dates modal (opened by clicking a plan card) */}
      {editPlanModalOpen && editingPlanId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => { if (!editPlanSaving) { setEditPlanModalOpen(false); setEditPlanError(""); } }}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 mb-4">Edit plan</h3>
            {editPlanError && (
              <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                {editPlanError}
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Plan name</label>
                <input
                  type="text"
                  value={editPlanName}
                  onChange={(e) => { setEditPlanName(e.target.value); setEditPlanError(""); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="e.g. Week 1 – High protein"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start date</label>
                  <input
                    type="date"
                    value={editPlanStartDate}
                    onChange={(e) => { setEditPlanStartDate(e.target.value); setEditPlanError(""); }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End date</label>
                  <input
                    type="date"
                    value={editPlanEndDate}
                    onChange={(e) => { setEditPlanEndDate(e.target.value); setEditPlanError(""); }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                disabled={editPlanSaving}
                onClick={() => { setEditPlanModalOpen(false); setEditPlanError(""); }}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={editPlanSaving || !editPlanName.trim() || !editPlanStartDate || !editPlanEndDate}
                onClick={handleSaveEditPlan}
                className="px-4 py-2 rounded-lg bg-dark-orange text-white hover:bg-orange-700 disabled:opacity-50"
              >
                {editPlanSaving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF viewer modal: view then download */}
      {pdfModalOpen && pdfBlobUrl && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black/70 p-4" role="dialog" aria-modal="true" aria-label="View PDF">
          <div className="flex items-center justify-between gap-4 py-2 px-1">
            <h3 className="text-lg font-bold text-white">Diet plan PDF</h3>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleDownloadPdfFromModal}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-dark-orange text-white font-medium text-sm hover:bg-orange-700"
              >
                <FileDown className="w-4 h-4" />
                Download
              </button>
              <button
                type="button"
                onClick={closePdfModal}
                className="p-2 rounded-lg bg-white/10 text-white hover:bg-white/20"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
          </div>
          <div className="flex-1 min-h-0 rounded-lg overflow-hidden bg-white">
            <iframe
              src={pdfBlobUrl}
              title="Diet plan PDF"
              className="w-full h-full min-h-[70vh]"
            />
          </div>
        </div>
      )}

      {/* Date strip: only in Tracked tab */}
      {!isPlannedView && (
        <div className="relative mb-6 px-2">
          <div className="flex overflow-x-auto py-3 scrollbar-hide gap-2">
            {visibleDates.map((date, _index) => {
              const dateStr = formatDate(date);
              return (
                <DateBox
                  key={dateStr}
                  date={date.getDate()}
                  month={date.toLocaleString("default", { month: "short" })}
                  year={date.getFullYear()}
                  selected={selectedDate === dateStr}
                  OnSelect={() => handleOnSelectDate(dateStr)}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Meals Section */}
      <div className="w-full px-2">
        {!isPlannedView ? (
          isTrackingLoading ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : (
            renderMealSection(
              nutritionData.meals,
              openTracked,
              setOpenTracked,
              true,
              nutritionData.meal_windows
            )
          )
        ) : !isPlanDetailPage ? (
          null
        ) : planDetailsLoading && !planDetails ? (
          <>
            <div className="mb-4 px-2 py-3 bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
                <span className="text-lg font-semibold text-gray-900">{planDisplayMeta.title}</span>
                {planDisplayMeta.startStr ? (
                  <span className="text-gray-600">
                    Start:{" "}
                    <span className="font-medium text-gray-800">
                      {formatDateForDisplay(planDisplayMeta.startStr)}
                    </span>
                  </span>
                ) : null}
                {planDisplayMeta.endStr ? (
                  <span className="text-gray-600">
                    End:{" "}
                    <span className="font-medium text-gray-800">
                      {formatDateForDisplay(planDisplayMeta.endStr)}
                    </span>
                  </span>
                ) : null}
              </div>
            </div>
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          </>
        ) : planDetails ? (
          <>
            <div className="mb-4 px-2 py-3 bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
                <span className="text-lg font-semibold text-gray-900">{planDisplayMeta.title}</span>
                {planDisplayMeta.startStr ? (
                  <span className="text-gray-600">
                    Start:{" "}
                    <span className="font-medium text-gray-800">
                      {formatDateForDisplay(planDisplayMeta.startStr)}
                    </span>
                  </span>
                ) : null}
                {planDisplayMeta.endStr ? (
                  <span className="text-gray-600">
                    End:{" "}
                    <span className="font-medium text-gray-800">
                      {formatDateForDisplay(planDisplayMeta.endStr)}
                    </span>
                  </span>
                ) : null}
              </div>
            </div>
            <MealSlotEditorToolbar
              marginClassName="-mx-2 px-2"
              topRow={
                <>
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <h2 className="text-lg font-bold text-gray-900 whitespace-nowrap">Planned meals</h2>
                    <MealSlotViewModeToggle
                      viewMode={planViewMode}
                      onToggle={() => {
                        const next = planViewMode === "options-first" ? "meal-types-first" : "options-first";
                        setPlanViewMode(next);
                        if (next === "meal-types-first") {
                          const first =
                            planPeriods.find((p) => {
                              const ids = planDataByPeriod[p]?.optionIds || {};
                              return planMealIdMap[normalizeKey(p)] && Object.keys(ids).length > 0;
                            }) ||
                            planPeriods.find((p) => planMealIdMap[normalizeKey(p)]);
                          setSelectedMealTypeFilter(first || null);
                          setSelectedOptionFilter(null);
                        } else {
                          setSelectedOptionFilter(planAvailableOptions[0] || "1");
                          setSelectedMealTypeFilter(null);
                        }
                      }}
                    />
                  </div>
                  <CompactNutrientSummaryCard
                    label="Average per option"
                    totals={transformTotalsForCompactCard(planToolbarNutrition.averageAcrossOptions)}
                  />
                  <NutrientSummaryCard
                    compact
                    eventId={eventTypeId}
                    profileId={profileId}
                    setToastMessage={setToastMessage}
                    setToastType={setToastType}
                    subtitle={planViewMode === "options-first" ? `Option ${selectedOptionFilter}` : (selectedMealTypeFilter ? `${selectedMealTypeFilter} (avg)` : "Selected")}
                    totals={transformTotalsForCompactCard(planToolbarNutrition.selectedContext)}
                    settings={nutritionData.settings}
                    onSettingsSaved={(vals) =>
                      setNutritionData((prev) => ({ ...prev, settings: vals }))
                    }
                  />
                  <button
                    type="button"
                    onClick={handleViewPDF}
                    disabled={isDownloadingPDF}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-dark-orange text-white font-medium text-sm hover:bg-orange-700 disabled:opacity-50 shrink-0"
                  >
                    <FileDown className="w-4 h-4" />
                    {isDownloadingPDF ? "Generating…" : "View PDF"}
                  </button>
                  <button
                    type="button"
                    onClick={handleOpenSaveAsTemplate}
                    disabled={!activePlanId || pendingSyncTotal > 0}
                    title={pendingSyncTotal > 0 ? "Wait for pending changes to finish syncing." : undefined}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-orange-primary text-orange-primary font-medium text-sm hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                  >
                    <Layers className="w-4 h-4" />
                    Save as new template
                  </button>
                  {activePlanId ? (
                    <PendingSyncIndicator scopeKey={`plan-${activePlanId}`} />
                  ) : null}
                </>
              }
              filterRow={
                planViewMode === "options-first" ? (
                  <MealSlotOptionFilterBar
                    options={planDisplayOptions}
                    selectedOption={selectedOptionFilter}
                    onSelectOption={setSelectedOptionFilter}
                    onAddOptionClick={() =>
                      setAddChoiceModal({
                        open: true,
                        title: "Add option",
                        items: [
                          ...planEmptyOptions.map((n) => ({
                            value: n,
                            label: `Option ${n} (no foods yet – select to add)`,
                          })),
                          { value: "__new__", label: "Add new option" },
                        ],
                        onSelect: (item) => {
                          const value = item?.value ?? item;
                          if (value === "__new__") {
                            handlePlanAddOptionAll();
                          } else {
                            setSelectedOptionFilter(String(value));
                          }
                        },
                      })
                    }
                    onDeleteOption={requestPlanDeleteOptionConfirm}
                    deletingOption={isDeletingOption ? optionDeleteConfirm.optionNum : undefined}
                    disabled={optionDeleteConfirm.open}
                  />
                ) : (
                  <MealSlotMealTypeFilterBar
                    mealTypes={planPeriods.filter((p) => planMealIdMap[normalizeKey(p)])}
                    selectedMealType={selectedMealTypeFilter}
                    onSelectMealType={setSelectedMealTypeFilter}
                  />
                )
              }
            />

            {planViewMode === "options-first" && selectedOptionFilter ? (
              <div className="space-y-0">
                {(() => {
                  const mealTypesForOption = [];
                  const inPlanWithoutOption = [];
                  planPeriods.forEach((period) => {
                    const hasMeal = planMealIdMap[normalizeKey(period)];
                    const hasThisOption = optionIdsHas(
                      planDataByPeriod[period]?.optionIds,
                      selectedOptionFilter
                    );
                    if (hasThisOption) mealTypesForOption.push(period);
                    else if (hasMeal) {
                      inPlanWithoutOption.push({
                        value: period,
                        label: period,
                        isNew: false,
                      });
                    }
                  });

                  const inPlanKeys = new Set((planDetails.meals || []).map((m) => normalizeKey(m.meal_type_description || m.meal_type)));
                  const utilsMealTypes = dietCommonUtils?.meal_types || [];
                  const notInPlanFromPeriods = MEALPERIODS.filter((period) => {
                    if (inPlanKeys.has(normalizeKey(period))) return false;
                    const match = utilsMealTypes.find((mt) => normalizeKey(mt.description || "") === normalizeKey(period));
                    return !!match;
                  }).map((period) => {
                    const mt = utilsMealTypes.find((m) => normalizeKey(m.description || "") === normalizeKey(period));
                    return { value: mt?.description ?? period, label: `${period} (new)`, isNew: true };
                  });
                  const notInPlanFromUtils = utilsMealTypes
                    .filter((mt) => {
                      const key = normalizeKey(mt.description || "");
                      if (!key || inPlanKeys.has(key)) return false;
                      return !MEALPERIODS.some((p) => normalizeKey(p) === key);
                    })
                    .map((mt) => ({ value: mt.description || "", label: `${mt.description || ""} (new)`, isNew: true }));
                  const availableToAdd = [...inPlanWithoutOption, ...notInPlanFromPeriods, ...notInPlanFromUtils];

                  if (mealTypesForOption.length === 0) {
                    return (
                      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                        <p className="text-gray-500 mb-6">
                          No meal types in Option {selectedOptionFilter}. Add one to add food items.
                        </p>
                        {availableToAdd.length > 0 ? (
                          <button
                            type="button"
                            onClick={() =>
                              setAddChoiceModal({
                                open: true,
                                title: `Add meal type to Option ${selectedOptionFilter}`,
                                items: availableToAdd,
                                onSelect: (item) => {
                                  if (!item || item.value === "__none__") return;
                                  if (item.isNew) {
                                    handleAddMealTypeToPlan(item.value, selectedOptionFilter);
                                  } else {
                                    handlePlanAddOptionToMealType(item.value, selectedOptionFilter);
                                  }
                                },
                              })
                            }
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-gray-300 text-gray-600 hover:border-orange-primary hover:bg-orange-50 transition-colors font-medium focus:outline-none focus:ring-0"
                          >
                            Add meal type
                          </button>
                        ) : null}
                      </div>
                    );
                  }

                  return (
                    <MealSlotOptionsFirstRows
                      mealTypes={mealTypesForOption}
                      getRowMeta={(mealType) => {
                        const { foods = [] } = planDataByPeriod[mealType] || { optionIds: {}, foods: [] };
                        const foodsForOption = filterFoodsByOptionNumber(foods, selectedOptionFilter);
                        return {
                          itemCount: foodsForOption.length,
                          ...getRowNutrition(
                            planSummary,
                            mealType,
                            selectedOptionFilter,
                            foodsForOption
                          ),
                        };
                      }}
                      renderSlotEditor={(mealType, summary) => {
                        const { optionIds, foods } = planDataByPeriod[mealType] || { optionIds: {}, foods: [] };
                        return (
                          <DietMealSlotEditor
                            summary={summary}
                            intakeWindow={planMealWindows[normalizeKey(mealType)]}
                            onSaveIntakeWindow={(w) => handleUpdateMealIntakeWindow(mealType, w)}
                            periodName={mealType}
                            context="plan"
                            clientId={profileId}
                            targetPlanId={activePlanId}
                            eventTypeId={dietEventTypeId}
                            mealOptionIds={optionIds}
                            foodData={foods}
                            setAlertMessage={setToastMessage}
                            setAlertType={setToastType}
                            onPeriodDataChange={handlePlanPeriodDataChange}
                            selectedOption={selectedOptionFilter}
                            alwaysExpanded
                            dataByPeriod={planDataByPeriod}
                          />
                        );
                      }}
                      onAddClick={() =>
                        setAddChoiceModal({
                          open: true,
                          title: `Add meal type to Option ${selectedOptionFilter}`,
                          items: availableToAdd.length > 0 ? availableToAdd : [{ value: "__none__", label: "All meal types already in plan." }],
                          onSelect: (item) => {
                            if (!item || item.value === "__none__") return;
                            if (item.isNew) {
                              handleAddMealTypeToPlan(item.value, selectedOptionFilter);
                            } else {
                              handlePlanAddOptionToMealType(item.value, selectedOptionFilter);
                            }
                          },
                        })
                      }
                      addButtonTitle="Add meal type"
                    />
                  );
                })()}
              </div>
            ) : planViewMode === "meal-types-first" && selectedMealTypeFilter ? (
              <div className="space-y-0">
                {(() => {
                  const optionsForMeal = planDataByPeriod[selectedMealTypeFilter]?.optionIds || {};
                  const optionNumbers = extractOptionNumbersFromOptionIds(optionsForMeal);
                  const optionsWithoutThisMeal = planAvailableOptions.filter(
                    (n) => !optionIdsHas(optionsForMeal, n)
                  );
                  const { optionIds, foods } = planDataByPeriod[selectedMealTypeFilter] || { optionIds: {}, foods: [] };

                  return (
                    <MealSlotMealTypesFirstRows
                      selectedMealType={selectedMealTypeFilter}
                      optionNumbers={optionNumbers}
                      getRowMeta={(optNum) => {
                        const foodsForOpt = filterFoodsByOptionNumber(foods, optNum);
                        return {
                          itemCount: foodsForOpt.length,
                          ...getRowNutrition(
                            planSummary,
                            selectedMealTypeFilter,
                            optNum,
                            foodsForOpt
                          ),
                        };
                      }}
                      renderSlotEditor={(optNum, summary) => (
                        <DietMealSlotEditor
                          summary={summary}
                          intakeWindow={planMealWindows[normalizeKey(selectedMealTypeFilter)]}
                          onSaveIntakeWindow={(w) =>
                            handleUpdateMealIntakeWindow(selectedMealTypeFilter, w)
                          }
                          periodName={selectedMealTypeFilter}
                          context="plan"
                          clientId={profileId}
                          targetPlanId={activePlanId}
                          eventTypeId={dietEventTypeId}
                          mealOptionIds={optionIds}
                          foodData={foods}
                          setAlertMessage={setToastMessage}
                          setAlertType={setToastType}
                          onPeriodDataChange={handlePlanPeriodDataChange}
                          selectedOption={optNum}
                          alwaysExpanded
                          dataByPeriod={planDataByPeriod}
                        />
                      )}
                      showAddButton={optionsWithoutThisMeal.length > 0}
                      onAddClick={() =>
                        setAddChoiceModal({
                          open: true,
                          title: `Add option to ${selectedMealTypeFilter}`,
                          items: optionsWithoutThisMeal.map((n) => ({ value: n, label: `Option ${n}` })),
                          onSelect: (item) => {
                            const value = item?.value ?? item;
                            if (value) handlePlanAddOptionToMealType(selectedMealTypeFilter, value);
                          },
                        })
                      }
                      addButtonTitle="Add option"
                    />
                  );
                })()}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
                <p className="text-gray-500">Select an option or meal type above.</p>
              </div>
            )}

            <MealSlotChoiceModal
              open={addChoiceModal.open}
              title={addChoiceModal.title}
              items={addChoiceModal.items}
              onClose={() => setAddChoiceModal((p) => ({ ...p, open: false }))}
              onSelect={addChoiceModal.onSelect}
            />
          </>
        ) : (
          renderMealSection(
            plannedData.meals,
            openPlanned,
            setOpenPlanned,
            false,
            plannedData.meal_windows
          )
        )}
      </div>
    </div>
  );
}
